import { createSelector } from "reselect"
import { get, map, find, chain, filter } from "lodash"
import pluralize from "pluralize"
import { FACTORS_THRESHOLD } from "helpers/analyzeConstants"
import { computeAvailableSelectViewFactors, getFactorCategory, getFactorLabel, getIsAggregatedFactor, makeNonZeroKeysSelectorFromData } from "helpers/factors"
import { getFactorsMetadata, getCategories, getSelectedViews } from "selectors/factors"
import { getFavoriteFactors } from "modules/portfolio-root/selectors/portfolios"
import { getModelFactorsClassification } from "selectors/modelMetadata"
import { getCurrentPerformance, getRawPerformanceHistory } from "./performance"

export const OVERALL_ITEMS = [{
  id: "total",
  color: "#00abe8",
  label: "Total",
  field: "total",
  type: "overall",
}, {
  id: "factor",
  color: "#9067a7",
  label: "Factor",
  field: "factorsTotal",
  type: "overall",
}, {
  id: "alpha",
  color: "#627b8c",
  label: "Alpha",
  field: "alpha",
  type: "overall",
}, {
  id: "tradingPnl",
  color: "#da7c30",
  label: "Trading",
  field: "trading",
  type: "overall",
}]

const getRawDisplayType = state => get(state, "modules.analyzePerformance.displayType")
export const getDisabled = state => get(state, "modules.analyzePerformance.disabled")

export const getDisplayType = createSelector(
  [getRawDisplayType, getCategories],
  (displayType, categories) => {
    if (displayType === "total" || displayType === "factors") {
      return displayType
    } else if (find(categories, cat => cat.id === displayType)) {
      return displayType
    }

    return "total"
  },
)

export function computeModelFactors(factorsMetadata, lastDatum, disabled, favoriteFactors) {
  return chain(lastDatum)
    .get("factors")
    .map((performance, id) => ({
      id,
      lastValue: performance,
      label: getFactorLabel(factorsMetadata, id),
      category: getFactorCategory(factorsMetadata, id),
      favorite: favoriteFactors[id],
      field: `factors.${id}`,
      disabled: !!disabled[id],
      type: "factors",
      isAggregated: getIsAggregatedFactor(factorsMetadata, id),
    }))
    .value()
}

export const getComputedFactors = createSelector(
  [getFactorsMetadata, getCurrentPerformance, getDisabled, getFavoriteFactors],
  computeModelFactors,
)

const getNonZerosKeys = makeNonZeroKeysSelectorFromData({
  getter: (value) => get(value, "attribution.factors"),
  isAboveZeroTreshold: (value) => Math.abs(value) >= FACTORS_THRESHOLD,
  dataSelector: getRawPerformanceHistory,
  favoriteSelector: getFavoriteFactors,
  factorValueField: "return.cumulativePercent",
})

export const getAvailableSelectViewFactors = createSelector(
  [getComputedFactors, getModelFactorsClassification, getSelectedViews, getNonZerosKeys],
  computeAvailableSelectViewFactors,
)

export const getOverallItems = createSelector(
  [getCategories, getDisabled],
  (categories, disabled) => {
    return [
      ...map(OVERALL_ITEMS, item => ({ ...item, disabled: !!disabled[item.id] })),
      {
        id: "factor_total",
        color: "#9067a7",
        label: "All Factors",
        categoryLabel: "Factors",
        field: "factorsTotal",
        disabled: !!disabled.factor_total,
        type: "category",
      },
      ...chain(categories)
        .filter(category => !category.isSelectedView)
        .map(category => ({
          id: category.id,
          color: category.color,
          label: category.label,
          field: `categories.${category.id}`,
          disabled: !!disabled[category.id],
          type: "category",
        })),
      ...map(categories, category => ({
        id: `${category.id}_total`,
        color: category.color,
        categoryLabel: category.isSelectedView ? category.label : pluralize(category.label),
        category: category.id,
        label: category.isSelectedView ? category.label : `All ${category.label} Factors`,
        field: `categories.${category.id}`,
        disabled: !!disabled[`${category.id}_total`],
      })),
    ]
  },
)

export const getAllItems = createSelector(
  [getComputedFactors, getOverallItems],
  (factors, overallItems) => ([
    ...overallItems,
    ...factors,
  ]),
)

export const getAllCategoryItems = createSelector(
  [getComputedFactors, getOverallItems, getDisplayType],
  (factors, overallItems, displayType) => {
    switch (displayType) {
      case "total":
        return filter(overallItems, { type: "overall" })
      case "factors":
        return chain(overallItems)
          .filter({ type: "category" })
          .value()
      default: {
        const overall = chain(overallItems)
          .filter({ category: displayType })
          .value()

        return [
          ...overall,
          ...chain(factors)
            .filter({ category: displayType })
            .orderBy([item => Math.abs(item.lastValue)], ["desc"])
            .value(),
        ]
      }
    }
  },
)

export const getOrderedCategoryItems = createSelector(
  [getAllCategoryItems, getDisplayType],
  (items, displayType) => {
    if (displayType !== "total" && displayType !== "factors") {
      const overall = items[0]
      const factors = items.slice(1)
      return [
        overall,
        ...chain(factors)
          .orderBy([item => item.favorite, item => Math.abs(item.lastValue)], ["asc", "desc"])
          .value(),
      ]
    }

    return items
  },
)
